/**
 * PolarBear — interactive polar bear SVG.
 *
 * Ports the cream.html bear: pupils track the mouse cursor, clicking the bear
 * waves its paw, and a tip bubble cycles through copy. All wiring uses React
 * refs + state with cleaned-up listeners/intervals — no getElementById.
 */
import { useEffect, useRef, useState } from 'react';

const TIPS = [
  "Hi! I'm made of GHK-Cu and good vibes.",
  'Copper peptides + cold = happy skin.',
  'Keep me cool. Keep your skin cooler.',
  'I helped Dr. Pickart since 1973. Sort of.',
  "Pat the cream in — don't rub. Trust the bear.",
  "Firmer skin in ~12 weeks. I'll wait.",
];

// Resting pupil centers (match the eye whites in the SVG).
const BASE_PUPILS = [
  { x: 108, y: 84 },
  { x: 152, y: 84 },
];

export default function PolarBear() {
  const bearRef = useRef(null);
  const pupilRefs = [useRef(null), useRef(null)];

  const [tipIndex, setTipIndex] = useState(0);
  const [tipVisible, setTipVisible] = useState(false);
  const [waving, setWaving] = useState(false);
  const waveTimer = useRef(null);

  // Pupil tracking — listen on the document, move pupils via refs.
  useEffect(() => {
    const onMove = (e) => {
      const bear = bearRef.current;
      if (!bear) return;
      const r = bear.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height * 0.38;
      const ang = Math.atan2(e.clientY - cy, e.clientX - cx);
      const d = 3.2;
      pupilRefs.forEach((ref, i) => {
        const p = ref.current;
        if (!p) return;
        p.setAttribute('cx', BASE_PUPILS[i].x + Math.cos(ang) * d);
        p.setAttribute('cy', BASE_PUPILS[i].y + Math.sin(ang) * d);
      });
    };
    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tip bubble cycling.
  useEffect(() => {
    const id = setInterval(() => {
      setTipIndex((i) => (i + 1) % TIPS.length);
    }, 3600);
    return () => clearInterval(id);
  }, []);

  // Clean up the wave timeout on unmount.
  useEffect(() => () => clearTimeout(waveTimer.current), []);

  const handleBearClick = () => {
    setTipVisible(true);
    setWaving(true);
    clearTimeout(waveTimer.current);
    waveTimer.current = setTimeout(() => setWaving(false), 1500);
    // advance to next tip on each tap
    setTipIndex((i) => (i + 1) % TIPS.length);
  };

  return (
    <section
      style={{
        position: 'relative',
        zIndex: 1,
        height: 430,
        marginTop: 30,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 18,
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#bcd6f5',
          fontSize: 12,
          letterSpacing: '.16em',
          textTransform: 'uppercase',
          opacity: 0.85,
          zIndex: 5,
          fontFamily: "'Courier New',ui-monospace,monospace",
        }}
      >
        Move your mouse · tap the bear
      </div>

      {/* tip bubble */}
      <div
        style={{
          position: 'absolute',
          bottom: 300,
          left: '50%',
          transform: tipVisible
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(10px)',
          background: '#fff',
          color: '#0a1730',
          padding: '14px 20px',
          borderRadius: 18,
          maxWidth: 300,
          textAlign: 'center',
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.45,
          boxShadow: '0 14px 40px rgba(0,0,0,.4)',
          opacity: tipVisible ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity .3s, transform .3s',
          zIndex: 6,
        }}
      >
        {TIPS[tipIndex]}
        <span
          style={{
            position: 'absolute',
            bottom: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderTop: '10px solid #fff',
          }}
        />
      </div>

      {/* snow ground */}
      <svg
        viewBox="0 0 1440 220"
        preserveAspectRatio="none"
        style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 220, zIndex: 1 }}
      >
        <path
          d="M0 90 C 200 50 360 120 560 95 C 760 70 920 130 1120 100 C 1280 76 1440 110 1440 110 L1440 220 L0 220 Z"
          fill="#dcefff"
        />
        <path
          d="M0 130 C 240 100 420 160 640 135 C 860 110 1040 165 1240 140 C 1360 126 1440 150 1440 150 L1440 220 L0 220 Z"
          fill="#ffffff"
        />
      </svg>

      {/* polar bear */}
      <svg
        ref={bearRef}
        viewBox="0 0 260 240"
        xmlns="http://www.w3.org/2000/svg"
        onClick={handleBearClick}
        style={{
          position: 'absolute',
          bottom: 78,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 260,
          cursor: 'pointer',
          zIndex: 3,
          filter: 'drop-shadow(0 12px 20px rgba(10,23,48,.5))',
        }}
      >
        <defs>
          <radialGradient id="fur" cx="42%" cy="35%" r="75%">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#dfedef" />
          </radialGradient>
          <linearGradient id="fur2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#cfe2f2" />
          </linearGradient>
        </defs>

        {/* body */}
        <ellipse cx="130" cy="178" rx="78" ry="56" fill="url(#fur2)" />
        {/* legs */}
        <ellipse cx="92" cy="214" rx="26" ry="18" fill="url(#fur2)" />
        <ellipse cx="168" cy="214" rx="26" ry="18" fill="url(#fur2)" />
        {/* waving paw (left arm raised) */}
        <g
          style={{
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animation: waving ? 'paw-wave .5s ease-in-out 3' : 'none',
          }}
        >
          <ellipse cx="66" cy="150" rx="20" ry="26" fill="url(#fur2)" transform="rotate(20 66 150)" />
        </g>
        {/* right arm */}
        <ellipse cx="196" cy="170" rx="20" ry="28" fill="url(#fur2)" transform="rotate(-12 196 170)" />
        {/* head */}
        <circle cx="130" cy="92" r="62" fill="url(#fur)" />
        {/* ears */}
        <circle cx="86" cy="44" r="20" fill="url(#fur2)" />
        <circle cx="174" cy="44" r="20" fill="url(#fur2)" />
        <circle cx="86" cy="44" r="9" fill="#bcd6ee" />
        <circle cx="174" cy="44" r="9" fill="#bcd6ee" />
        {/* snout area */}
        <ellipse cx="130" cy="112" rx="34" ry="28" fill="#ffffff" />
        {/* eyes (whites) */}
        <circle cx="108" cy="84" r="12" fill="#fff" stroke="#cfe2f2" strokeWidth="1.5" />
        <circle cx="152" cy="84" r="12" fill="#fff" stroke="#cfe2f2" strokeWidth="1.5" />
        {/* pupils (move with cursor) */}
        <circle ref={pupilRefs[0]} cx="108" cy="84" r="6" fill="#16314f" />
        <circle ref={pupilRefs[1]} cx="152" cy="84" r="6" fill="#16314f" />
        {/* nose + mouth */}
        <ellipse cx="130" cy="106" rx="11" ry="8" fill="#16314f" />
        <path
          d="M130 114 Q 130 126 118 128 M130 114 Q 130 126 142 128"
          stroke="#16314f"
          strokeWidth="2.4"
          fill="none"
          strokeLinecap="round"
        />
        {/* little copper droplet on chest */}
        <circle cx="130" cy="172" r="9" fill="#e2a06a" opacity=".9" />
        <text x="130" y="176" fontSize="9" textAnchor="middle" fill="#fff" fontFamily="monospace">
          Cu
        </text>
      </svg>

      <style>{`
        @keyframes paw-wave {
          0%, 100% { transform: rotate(0); }
          50% { transform: rotate(-22deg); }
        }
      `}</style>
    </section>
  );
}
